import { ALARM_SEVERITY } from '../../../../../../Utils/AlarmsService';
import { resolveEquipmentStatusFlags } from '../equipmentStatusFlags';

export interface SeverityInfo {
    id: number;
    name: string;
    /** Cor do sistema (hex sem '#'). */
    color: string;
    /** Ranking de severidade — maior = mais severo (coluna `level` de `alarm_severity`). */
    level: number;
}

/**
 * Catálogo fixo das severidades (espelha a tabela de sistema `alarm_severity`).
 * "Não se aplica" (id 5) fica de fora de propósito — é o "não se aplica/evento", que não
 * caracteriza alarme e por isso não entra na maior severidade.
 */
const SEVERITY_CATALOG: Record<number, SeverityInfo> = {
    [ALARM_SEVERITY.URGENT]: { id: ALARM_SEVERITY.URGENT, name: 'Urgente', color: '000000', level: 6 },
    [ALARM_SEVERITY.CRITICAL]: { id: ALARM_SEVERITY.CRITICAL, name: 'Crítica', color: 'f14e4e', level: 5 },
    [ALARM_SEVERITY.HIGH]: { id: ALARM_SEVERITY.HIGH, name: 'Alta', color: 'ffcc78', level: 4 },
    [ALARM_SEVERITY.MEDIUM]: { id: ALARM_SEVERITY.MEDIUM, name: 'Média', color: 'ffe00a', level: 3 },
    [ALARM_SEVERITY.LOW]: { id: ALARM_SEVERITY.LOW, name: 'Baixa', color: '80FFFF', level: 2 },
};

/** Maior severidade ativa por `level`; ids fora do catálogo (ex.: Não Apl.) são ignorados. */
export const highestSeverity = (severityIds: number[]): SeverityInfo | null => {
    const applicable = severityIds
        .map((id) => SEVERITY_CATALOG[id])
        .filter((info): info is SeverityInfo => info != null);
    if (applicable.length === 0) {
        return null;
    }
    return applicable.reduce((max, info) => (info.level > max.level ? info : max));
};

export type AlarmStatusCardState = 'UNAVAILABLE' | 'INHIBITED' | 'NORMALIZED' | 'ALARMED';

export interface AlarmStatusResolution {
    state: AlarmStatusCardState;
    text: string;
    subText?: string;
    disabled: boolean;
    /** Severidade ativa de maior nível — presente apenas em `ALARMED`. */
    severity?: SeverityInfo;
}

/**
 * Regra 3: o card fica desabilitado em Sem comunicação, Sincronizando e Inibido (Sem com. e
 * Sincronizando compartilham o texto; Inibido tem o seu + chip). Comunicando -> Normalizado (sem
 * alarme) ou Alarmado (maior severidade ativa). Aceita `status` como nome ou bits do ETS — ver
 * `equipmentStatusFlags`. Inibido tem precedência sobre "sem comunicação" (como no backend).
 */
export const resolveAlarmStatusCard = (
    status: string | undefined,
    activeSeverityIds: number[]
): AlarmStatusResolution => {
    const flags = resolveEquipmentStatusFlags(status);

    if (flags.inhibited) {
        return {
            state: 'INHIBITED',
            text: 'Equipamento inibido',
            subText: 'Detecção de alarmes indisponível',
            disabled: true,
        };
    }
    if (flags.synchronizing || flags.withoutCommunication) {
        return {
            state: 'UNAVAILABLE',
            text: 'Alarmes indisponíveis',
            subText: 'Comunicação ausente',
            disabled: true,
        };
    }

    const severity = highestSeverity(activeSeverityIds);
    if (severity == null) {
        return { state: 'NORMALIZED', text: 'Sem alarmes ativos', disabled: false };
    }
    return { state: 'ALARMED', text: 'Alarmes ativos', disabled: false, severity };
};
